import React from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, X, Heart, ChevronRight } from "lucide-react";
import { useCart } from "../CartContext";
import { useAuth } from "../AuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ucare-platform/artifacts/t51zdnza_ucare_logo_extracted.png";

export function Header({ storeName = "UCare Essentials", logoUrl = "", logoSize = 64 }) {
    const { count, setOpen } = useCart();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobile, setMobile] = React.useState(false);
    const [searchOpen, setSearchOpen] = React.useState(false);
    const [query, setQuery] = React.useState("");
    const searchInputRef = React.useRef(null);

    React.useEffect(() => {
        if (!mobile) return;
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") setMobile(false); };
        window.addEventListener("keydown", onKey);
        return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
    }, [mobile]);

    const openSearch = () => {
        setSearchOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 60);
    };

    const submitSearch = (e) => {
        e?.preventDefault();
        const q = query.trim();
        if (!q) return;
        navigate(`/shop?q=${encodeURIComponent(q)}`);
        setSearchOpen(false);
        setQuery("");
    };

    return (
        <header className="sticky top-0 z-40 bg-[var(--bg-soft)]/75 backdrop-blur-md border-b border-[var(--line-soft)]">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 h-[64px] md:h-[78px] flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 sm:gap-2.5 shrink-0" data-testid="logo-link">
                    <div
                        className="logo-box flex items-center justify-center shrink-0"
                        style={{ "--logo-size": `${logoSize || 64}px`, "--logo-size-m": `${Math.max(44, Math.round((logoSize || 64) * 0.85))}px` }}
                    >
                        <img
                            src={logoUrl || LOGO_URL}
                            alt={storeName}
                            className="w-full h-full object-contain"
                        />
                    </div>
                    <div className="leading-[1.05]">
                        <div className="font-serif text-[17px] sm:text-[18px] md:text-[22px] tracking-tight text-[var(--ink)]">UCare</div>
                        <div className="font-serif italic text-[9px] sm:text-[10px] md:text-[12px] text-[var(--ink-soft)] -mt-0.5 tracking-[0.16em] uppercase">Essentials</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-9 text-[14px] text-[var(--ink-soft)]">
                    <NavLink to="/" label="Home" testid="nav-home" />
                    <NavLink to="/shop" label="Shop" testid="nav-shop" />
                    <NavLink to="/learn" label="Learn" testid="nav-learn" />
                    <NavLink to="/consult-doctor" label="Consult a Doctor" testid="nav-consult" />
                    <NavLink to="/the-board" label="The Board" testid="nav-board" />
                    <NavLink to="/share-to-win" label="Share & Win" testid="nav-share-to-win" />
                    <NavLink to="/about" label="About Us" testid="nav-about" />
                </nav>

                <div className="flex items-center gap-3 md:gap-4">
                    {/* Inline search: icon → expands into an input */}
                    <div className="flex items-center" data-testid="header-search-wrap">
                        {searchOpen ? (
                            <form onSubmit={submitSearch} className="flex items-center gap-1.5">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onBlur={() => { if (!query) setSearchOpen(false); }}
                                    onKeyDown={(e) => { if (e.key === "Escape") { setSearchOpen(false); setQuery(""); } }}
                                    placeholder="Search products…"
                                    className="bg-transparent border-b border-[var(--ink-soft)] focus:border-[var(--ink)] outline-none text-[14px] text-[var(--ink)] placeholder:text-[var(--ink-soft)] w-[160px] sm:w-[220px] py-1 transition-all"
                                    data-testid="header-search-input"
                                />
                                <button type="submit" className="text-[var(--ink-soft)] hover:text-[var(--ink)]" aria-label="Search" data-testid="header-search-submit">
                                    <Search size={18} strokeWidth={1.4} />
                                </button>
                            </form>
                        ) : (
                            <button
                                type="button"
                                onClick={openSearch}
                                className="p-2 -m-1 text-[var(--ink-soft)] hover:text-[var(--ink)]"
                                aria-label="Open search"
                                data-testid="header-search-btn"
                            >
                                <Search size={20} strokeWidth={1.4} />
                            </button>
                        )}
                    </div>
                    {user && (
                        <Link to="/account#wishlist" className="hidden md:block text-[var(--ink-soft)] hover:text-[var(--terra)]" aria-label="Wishlist" data-testid="wishlist-btn">
                            <Heart size={20} strokeWidth={1.4} />
                        </Link>
                    )}
                    <button
                        onClick={() => setOpen(true)}
                        className="relative p-2 -m-1 text-[var(--ink-soft)] hover:text-[var(--ink)]"
                        data-testid="open-cart-btn"
                        aria-label="Cart"
                    >
                        <ShoppingBag size={20} strokeWidth={1.4} />
                        {count > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-[var(--taupe)] text-white text-[9px] font-bold rounded-full w-4 h-4 grid place-items-center" data-testid="cart-count">
                                {count}
                            </span>
                        )}
                    </button>
                    {user ? (
                        <div className="hidden md:flex items-center gap-3 text-[13px]">
                            <Link to={user.role === "admin" ? "/admin" : "/account"} className="flex items-center gap-1.5 text-[var(--ink)] hover:text-[var(--taupe)]" data-testid="account-link">
                                <User size={16} strokeWidth={1.4} />
                                <span>{user.name?.split(" ")[0]}</span>
                            </Link>
                            <button onClick={logout} className="text-[var(--body)] hover:text-[var(--ink)] text-[12px]" data-testid="logout-btn">
                                Sign out
                            </button>
                        </div>
                    ) : (
                        <Link to="/login" className="hidden md:flex items-center gap-1.5 text-[14px] text-[var(--ink)] hover:text-[var(--taupe)]" data-testid="login-link">
                            <User size={18} strokeWidth={1.4} />
                            <span>Login</span>
                        </Link>
                    )}
                    <button className="lg:hidden p-2 -m-1 text-[var(--ink)]" onClick={() => setMobile(true)} data-testid="open-mobile-menu" aria-label="Menu">
                        <Menu size={22} strokeWidth={1.4} />
                    </button>
                </div>
            </div>

            {/* Portal: header's backdrop-filter creates a containing block that would trap
                position:fixed — rendering into <body> keeps the menu truly fullscreen. */}
            {mobile && createPortal(
                <div className="fixed inset-0 z-[100] p-6 overflow-y-auto overscroll-contain" style={{ background: "var(--bg)", paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }} data-testid="mobile-menu">
                    <div className="flex items-center justify-between mb-8">
                        <span className="font-serif text-2xl text-[var(--ink)]">Menu</span>
                        <button onClick={() => setMobile(false)} data-testid="close-mobile-menu" className="p-1"><X size={22} /></button>
                    </div>
                    <nav className="flex flex-col gap-4 text-lg text-[var(--ink)]">
                        {[
                            ["Home", "/"], ["Shop", "/shop"], ["Learn", "/learn"],
                            ["Consult a Doctor", "/consult-doctor"], ["The Board", "/the-board"],
                            ["Share & Win", "/share-to-win"], ["About Us", "/about"],
                        ].map(([label, to]) => (
                            <Link key={to} to={to} onClick={() => setMobile(false)} className="flex items-center justify-between border-b border-[var(--line-soft)] pb-3">
                                {label} <ChevronRight size={16} />
                            </Link>
                        ))}
                        <hr className="border-[var(--line-soft)] my-2" />
                        {user ? (
                            <>
                                <Link to={user.role === "admin" ? "/admin" : "/account"} onClick={() => setMobile(false)}>{user.role === "admin" ? "Admin Panel" : "My Account"}</Link>
                                <button onClick={logout} className="text-left">Sign out</button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" onClick={() => setMobile(false)}>Login</Link>
                                <Link to="/register" onClick={() => setMobile(false)}>Create Account</Link>
                            </>
                        )}
                    </nav>
                </div>,
                document.body
            )}
        </header>
    );
}

function NavLink({ to, label, testid }) {
    return (
        <Link to={to} className="relative hover:text-[var(--ink)] transition" data-testid={testid}>
            {label}
        </Link>
    );
}

export function Footer({ settings }) {
    const [email, setEmail] = React.useState("");
    return (
        <footer className="bg-[var(--bg)] mt-24 border-t border-[var(--line-soft)]" data-testid="footer">
            {/* Trust strip */}
            <div className="border-b border-[var(--line-soft)] py-7">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-wrap justify-center gap-x-14 gap-y-4 text-[13px] text-[var(--ink-soft)]">
                    <span className="flex items-center gap-2"><span className="trust-circle">✕</span>No Artificial Fillers</span>
                    <span className="flex items-center gap-2"><span className="trust-circle">📋</span>Clinical Studies & Trials</span>
                    <span className="flex items-center gap-2"><span className="trust-circle">⚗️</span>Heavy Metal Tested</span>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 text-center">
                <div className="text-[13px] text-[var(--body)] mb-6">
                    © {new Date().getFullYear()} {settings?.store_name || "UCare Essentials"}. All rights reserved.
                </div>
                <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-[13px] text-[var(--ink-soft)] border-b border-[var(--line-soft)] pb-8">
                    <Link to="/privacy" className="hover:text-[var(--ink)]">Privacy Policy</Link>
                    <Link to="/terms" className="hover:text-[var(--ink)]">Terms & Conditions</Link>
                    <Link to="/privacy-ethics" className="hover:text-[var(--ink)]">Privacy & Ethics</Link>
                    <Link to="/medical-disclaimer" className="hover:text-[var(--ink)]">Medical Disclaimer</Link>
                    <Link to="/faq" className="hover:text-[var(--ink)]">Certifications & FAQ</Link>
                </div>

                <div className="pt-10 max-w-md mx-auto">
                    <h4 className="font-serif text-[22px] text-[var(--ink)] mb-2">Join the Wellness Circle</h4>
                    <p className="text-[13px] text-[var(--body)] mb-5">Expert-reviewed insights and early access to new rituals.</p>
                    <form
                        onSubmit={(e) => { e.preventDefault(); alert("Thanks — check your inbox."); setEmail(""); }}
                        className="flex gap-2"
                    >
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="your@email.com"
                            className="field flex-1"
                            data-testid="newsletter-input"
                        />
                        <button className="btn btn-primary" data-testid="newsletter-submit">Join</button>
                    </form>
                </div>

                <p className="text-[11px] text-[var(--body)]/80 max-w-3xl mx-auto mt-12 leading-relaxed">
                    *Medical Disclaimer: The products and information on this site are for luxury wellness purposes only and are not intended to diagnose, treat, cure, or prevent any disease. Consult with a qualified healthcare professional before starting any new wellness ritual. {settings?.store_name || "UCare Essentials"} prioritizes ethical sourcing and clinical integrity in every formulation.
                </p>
            </div>
        </footer>
    );
}

export function CartDrawer({ settings }) {
    const { items, open, setOpen, update, remove, subtotal, clear } = useCart();
    const navigate = useNavigate();
    const [coupon, setCoupon] = React.useState("");
    const [discount, setDiscount] = React.useState(0);
    const [couponMsg, setCouponMsg] = React.useState("");

    React.useEffect(() => {
        if (!open) return;
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
    }, [open, setOpen]);

    const freeAbove = settings?.free_shipping_above ?? 499;
    const shipping = subtotal === 0 || subtotal >= freeAbove ? 0 : 49;
    const total = Math.max(0, subtotal + shipping - discount);

    const applyCoupon = async () => {
        if (!coupon.trim()) return;
        try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/apply`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code: coupon, subtotal }),
            });
            const d = await r.json();
            if (!r.ok) { setCouponMsg(d.detail || "Invalid coupon"); setDiscount(0); }
            else { setDiscount(d.discount); setCouponMsg(`Saved ₹${Math.round(d.discount)}`); localStorage.setItem("uce_coupon", coupon.toUpperCase()); }
        } catch { setCouponMsg("Error applying coupon"); }
    };

    const goCheckout = () => {
        if (items.length === 0) return;
        if (discount > 0) localStorage.setItem("uce_coupon", coupon.toUpperCase());
        else localStorage.removeItem("uce_coupon");
        setOpen(false);
        navigate("/checkout");
    };

    if (!open) return null;
    return (
        <>
            <div className="drawer-backdrop" onClick={() => setOpen(false)} data-testid="cart-backdrop" />
            <aside className="drawer-panel" data-testid="cart-drawer">
                <div className="flex items-center justify-between p-6 border-b border-[var(--line-soft)]">
                    <div className="font-serif text-[26px] text-[var(--ink)]">Your Cart</div>
                    <button onClick={() => setOpen(false)} className="p-2 hover:bg-[var(--bg)] rounded-full text-[var(--ink-soft)]" data-testid="close-cart-btn">
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {items.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="text-5xl mb-4">🌸</div>
                            <p className="text-[var(--body)]">Your cart is empty.</p>
                            <button className="btn btn-outline btn-sm mt-6" onClick={() => { setOpen(false); navigate("/shop"); }}>
                                Browse products
                            </button>
                        </div>
                    ) : (
                        <ul className="space-y-5" data-testid="cart-items">
                            {items.map((it) => (
                                <li key={it.product_id} className="flex gap-3 items-center">
                                    <div className="w-20 h-20 rounded-lg bg-gradient-to-b from-[#f5ede1] to-[#d8c4a8] grid place-items-center shrink-0 overflow-hidden">
                                        {it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" /> : <span className="text-2xl">{it.emoji}</span>}
                                    </div>
                                    <div className="flex-1">
                                        <div className="prod-title text-[13px] leading-tight mb-1">{it.name}</div>
                                        <div className="text-[12px] text-[var(--body)]">₹{it.price}</div>
                                        <div className="qty mt-2">
                                            <button onClick={() => update(it.product_id, it.quantity - 1)}>−</button>
                                            <span>{it.quantity}</span>
                                            <button onClick={() => update(it.product_id, it.quantity + 1)}>+</button>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-serif text-[15px] text-[var(--ink)]">₹{Math.round(it.price * it.quantity)}</div>
                                        <button onClick={() => remove(it.product_id)} className="text-[11px] text-[var(--body)] hover:text-[var(--terra)] mt-1">Remove</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="border-t border-[var(--line-soft)] p-6 space-y-3 bg-[var(--bg)]" style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}>
                    {items.length > 0 && (
                        <>
                            <div className="flex gap-2">
                                <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" className="field flex-1" data-testid="coupon-input" />
                                <button onClick={applyCoupon} className="btn btn-outline btn-sm" data-testid="apply-coupon-btn">Apply</button>
                            </div>
                            {couponMsg && <div className={`text-[12px] ${discount > 0 ? "text-green-700" : "text-[var(--terra)]"}`}>{couponMsg}</div>}
                            <div className="space-y-1.5 text-[14px]">
                                <Row k="Subtotal" v={`₹${Math.round(subtotal)}`} />
                                <Row k="Shipping" v={shipping === 0 ? "Free" : `₹${shipping}`} />
                                {discount > 0 && <Row k={`Discount (${coupon})`} v={`−₹${Math.round(discount)}`} accent />}
                                <div className="h-px bg-[var(--line)] my-1" />
                                <Row k="Total" v={`₹${Math.round(total)}`} bold />
                            </div>
                            <button className="btn btn-primary w-full mt-2" onClick={goCheckout} data-testid="checkout-btn">
                                Pay Securely
                            </button>
                            <div className="text-center text-[11px] text-[var(--body)]">
                                🔒 Secured by Razorpay · UPI · Cards · NetBanking
                            </div>
                            <button onClick={clear} className="block mx-auto text-[12px] text-[var(--body)] hover:underline" data-testid="clear-cart-btn">Clear cart</button>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}

function Row({ k, v, accent, bold }) {
    return (
        <div className="flex justify-between">
            <span className={bold ? "font-medium text-[var(--ink)]" : "text-[var(--body)]"}>{k}</span>
            <span className={`${bold ? "font-serif text-[16px] text-[var(--ink)]" : "text-[var(--ink-soft)]"} ${accent ? "!text-[var(--terra)]" : ""}`}>{v}</span>
        </div>
    );
}

/* Page transition wrapper: re-mounts on route change with a soft fade-up */
export function PageTransition({ children }) {
    const location = useLocation();
    return (
        <div key={location.pathname} className="page-anim">
            {children}
        </div>
    );
}
